import React, { useState } from 'react';
import { useAppSettings } from '@/context/app-settings';
import { FontSourceSelector } from './typography/FontSourceSelector';
import { TypographySaveButton } from './typography/TypographySaveButton';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function TypographySettings() {
  const { settings, saving, saveSettings } = useAppSettings();
  const [fontSource, setFontSource] = useState<'google' | 'custom'>('google');
  const [selectedGoogleFont, setSelectedGoogleFont] = useState('Inter');
  const [customFontFile, setCustomFontFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  
  // Update local state when settings change
  React.useEffect(() => {
    if (settings?.typography) {
      setFontSource(settings.typography.source || 'google');
      setSelectedGoogleFont(settings.typography.googleFont || 'Inter');
    }
  }, [settings]);

  if (!settings) {
    return <div>Loading...</div>;
  }

  const handleFileUpload = async (file: File) => {
    try {
      setUploading(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${file.name}`;
      const filePath = `fonts/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('fonts')
        .upload(filePath, file);

      if (uploadError) {
        toast.error('Failed to upload font file');
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('fonts')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Font upload error:', error);
      toast.error('Failed to upload font file');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    let customFontUrl = settings.typography.customFontUrl;

    if (fontSource === 'custom' && customFontFile) {
      customFontUrl = await handleFileUpload(customFontFile);
      if (!customFontUrl) {
        return;
      }
    }

    const success = await saveSettings({
      typography: {
        source: fontSource,
        googleFont: fontSource === 'google' ? selectedGoogleFont : undefined,
        customFontUrl: fontSource === 'custom' ? customFontUrl : null
      }
    });

    if (success) {
      // Force a small delay to ensure the settings are saved before reloading
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  return (
    <div className="space-y-6">
      <FontSourceSelector
        fontSource={fontSource}
        onSourceChange={setFontSource}
        selectedGoogleFont={selectedGoogleFont}
        onGoogleFontChange={setSelectedGoogleFont}
        customFontFile={customFontFile}
        onCustomFontChange={setCustomFontFile}
        uploading={uploading}
        currentCustomFontUrl={settings.typography.customFontUrl}
      />

      <TypographySaveButton
        onSave={handleSave}
        saving={saving}
        uploading={uploading}
        fontSource={fontSource}
        selectedGoogleFont={selectedGoogleFont}
        customFontFile={customFontFile}
        config={settings}
      />
    </div>
  );
}